import { useCallback, useEffect, useState } from "react";
import Modal, { ModalSize } from "~/common/components/Modal";
import MessageContainerBox from "~/features/bulkAddToCart/components/MessageContainerBox";
import HelpSection from "~/features/bulkAddToCart/components/HelpSection";
import FileUploadSection from "~/features/bulkAddToCart/components/FileUploadSection";
import TextUploadSection from "~/features/bulkAddToCart/components/TextUploadSection";
import { type Result, sendData as sendText } from "~/features/bulkAddToCart/api/sendTextForValidation";
import { sendData as sendFile } from "~/features/bulkAddToCart/api/sendFileForValidation";
import { sendData as addToCart } from "~/features/bulkAddToCart/api/addToCart";
import Spinner from "~/common/components/Spinner/Spinner";
import { redirectToUrl, resolveCartPageUrl } from "~/utils/url/urlUtils";
import PageToolbar from "~/common/components/PageToolbar";
import { IconType } from "~/common/components/icon/IconType";
import { Button, Grid, Stack } from "~/common/mui";
import DeleteCartConfirmationModal from "~/domains/cart/components/DeleteCartConfirmationModal";
import {
  BulkUploadSource,
  BulkUploadState,
  type MessageModel,
  type Msg,
  type PreviewItem,
  type PreviewItemUpdateData,
} from "~/features/bulkAddToCart/types/models";
import { removeBulkPreviewEntry } from "~/features/bulkAddToCart/api/deletePreviewEntry";
import { updatePreviewEntry, updatePreviewEntryBulk } from "~/features/bulkAddToCart/api/updatePreviewEntry";
import BulkAtcPopupPreviewStep from "~/features/bulkAddToCart/components/BulkAtcPopupPreviewStep";
import * as GABulkUpload from "~/app/analytics/ga/bulkUpload";
import { useTranslation, type WithTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { apiFetchMergeCartData } from "~/features/bulkAddToCart/api/fetchMergeCartData";
import { apiClearMergeCartData } from "~/features/bulkAddToCart/api/clearMergeData";
import { FccAction, trackFccAction } from "~/features/freeCapacityPush/api/trackFccAction";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import type { RootState } from "~/app/store";
import { useSelector } from "react-redux";
import { Alert } from "~/common/mui/Alert/Alert";
import { ChoosePriceReferenceForCart } from "~/features/cart/cart/components/Buttons/ChoosePriceReferenceForCart";
import { SwitchProductVersions } from "~/features/cart/cart/components/Buttons/SwitchProductVersions";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { isTargetPriceEnabled } from "~/app/config/featureFlags";

interface BulkATCPopupProps {
  onClose: () => void;
  mergeCartFlow?: boolean;
}

const BulkATCPopup = ({ onClose, mergeCartFlow }: BulkATCPopupProps) => {
  const { t } = useTranslation();

  const [messages, setMessages] = useState<Msg[]>([]);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [state, setState] = useState<BulkUploadState>(
    mergeCartFlow ? BulkUploadState.UPLOADING : BulkUploadState.INITIAL,
  );
  const [source, setSource] = useState<BulkUploadSource>(BulkUploadSource.FILE);
  const [showOverrideConfirmation, setShowOverrideConfirmation] = useState(false);
  const [parsedItems, setParsedItems] = useState<PreviewItem[]>([]);
  const [showOnSaleProductsOnly, setOnSaleProductsOnly] = useState(false);

  const [snackbarData, setSnackbarData] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  const cartItemAmount = useSelector((state: RootState) => state.minicart.count);

  const apiUpdatePreviewEntry = async (indexToUpdate: number, updatedItem: PreviewItem) => {
    const serverItems = await updatePreviewEntry(indexToUpdate, updatedItem);
    const serverItem = serverItems[indexToUpdate];
    setParsedItems((prevItems) => {
      const newItems = [...prevItems];
      const oldItem = newItems[indexToUpdate];
      newItems[indexToUpdate] = {
        ...oldItem,
        ...serverItem,
        // keep local user edits (server can lag or omit the latest typed value)
        targetPrice: updatedItem.targetPrice,
        id: oldItem.id,
        marketType: oldItem.marketType,
        isCommentVisible: oldItem.isCommentVisible,
      };
      return newItems;
    });
  };

  const apiUpdatePreviewEntryBulk = async (updatedItems: PreviewItem[]) => {
    const serverItems = await updatePreviewEntryBulk(updatedItems);
    setParsedItems((prevItems) => {
      const newItems = prevItems.map((oldItem, index) => {
        const newItem = serverItems[index];
        return {
          ...oldItem,
          ...newItem,
          targetPrice: oldItem.targetPrice,
          id: oldItem.id,
          marketType: oldItem.marketType,
          isCommentVisible: oldItem.isCommentVisible,
        };
      });
      return newItems;
    });
  };

  const debouncedApiUpdatePreviewEntry = useCallback(
    debounce(apiUpdatePreviewEntry, 200, { leading: false, trailing: true }),
    [],
  );

  useEffect(() => {
    if (mergeCartFlow) {
      handleValidationResult(apiFetchMergeCartData());
    }
  }, [mergeCartFlow]);

  const handleOpenPriceRefSnackBar = (count: number) => {
    setSnackbarData({
      open: true,
      message: t("cartPage.modal.choosePriceReferenceForCart.snackbar", {
        count,
      }),
    });
  };

  const handleOpenSwitchProductSnackBar = (count: number) => {
    setSnackbarData({
      open: true,
      message: t("cartPage.modal.switchProductVersion.snackbar", { count }),
    });
  };

  const handleFileSelected = (file: File) => {
    setState(BulkUploadState.UPLOADING);
    setSource(BulkUploadSource.FILE);
    handleValidationResult(sendFile(file));
  };

  const handleTextSelected = (contents: string) => {
    setState(BulkUploadState.UPLOADING);
    setSource(BulkUploadSource.TEXT);
    handleValidationResult(sendText(contents));
  };

  const handleValidationResult = (call: Promise<Result>) => {
    call
      .then((result) => {
        if (!result.error) {
          const successfullyValidated = result.products.length;
          const uploadMsgKey = `bulkUpload.upload.success.${hasLoadedData ? "existing" : "new"}`;
          const uploadMsg = t(uploadMsgKey, { count: successfullyValidated });
          const message = { type: "info", label: uploadMsg } as {
            type: "info";
            label: string;
          };

          setHasLoadedData(successfullyValidated > 0);
          setMessages([message]);
          setParsedItems(result.products);
          setState(BulkUploadState.UPLOAD_SUCCESS);
        } else {
          setState(BulkUploadState.ERROR);
          setHasLoadedData(false);
          setMessages(resolveErrorLabels(t, result.error));
        }
      })
      .catch(() => {
        const message = {
          type: "error",
          label: t("bulkUpload.error.upload"),
        } as { type: "error"; label: string };
        setState(BulkUploadState.ERROR);
        setHasLoadedData(false);
        setMessages([message]);
      });
  };

  const doAddToCart = (saleOnly: boolean) => {
    trackFccAction(FccAction.BulkEntry);
    sendAddToCart(false, saleOnly);
  };

  const doOverrideCart = (saleOnly: boolean) => {
    doHideOverrideConfirmation();
    sendAddToCart(true, saleOnly);
  };

  const doShowOverrideConfirmation = () => {
    trackFccAction(FccAction.BulkEntry);
    setShowOverrideConfirmation(true);
  };

  const doHideOverrideConfirmation = () => setShowOverrideConfirmation(false);

  const sendAddToCart = (override: boolean, saleOnly: boolean) => {
    setState(BulkUploadState.LOADING_CART);
    addToCart(override, saleOnly)
      .then(({ productAmount, quantityAdjusted }) => {
        if (override) {
          GABulkUpload.overrideCart(productAmount);
        } else {
          GABulkUpload.addToCart(productAmount);
        }
        window.setTimeout(() => {
          const page = resolveCartPageUrl();
          const params = new URLSearchParams();
          params.append("bulkSuccess", productAmount.toString());
          params.append("quantityAdjusted", quantityAdjusted.toString());
          if (override) {
            params.append("override", "true");
          }
          redirectToUrl(`${page}?${params.toString()}`);
        }, 0);
      })
      .catch(() => setState(BulkUploadState.ERROR_CART));
  };

  const getMessages = (sourceType: BulkUploadSource) => {
    if (source !== sourceType || (state !== BulkUploadState.ERROR && state !== BulkUploadState.UPLOAD_SUCCESS)) {
      return [];
    }
    return messages;
  };

  const removeAllParsedItems = () => {
    setParsedItems([]);
    setState(BulkUploadState.INITIAL);
  };

  const removeParsedItem = (id: number) => {
    const indexToRemove = parsedItems.findIndex((item) => item.id === id);
    removeBulkPreviewEntry(indexToRemove);
    if (parsedItems.length === 1) {
      setParsedItems([]);
      setState(BulkUploadState.INITIAL);
    } else {
      if (showOnSaleProductsOnly && parsedItems.filter((item) => item.onSale).length == 1) {
        // Reset the toggle when:
        // sale only toggle is checked + only one onsale entry is visible
        // And that one last on sale entry is deleted.
        setOnSaleProductsOnly(false);
      }
      setParsedItems(parsedItems.filter((_, index) => index !== indexToRemove));
    }
  };

  const setAllParsedItemsCommentVisibility = (visible: boolean) => {
    setParsedItems((prevItems) => prevItems.map((item) => ({ ...item, isCommentVisible: visible })));
  };

  const setParsedItemCommentVisibility = (id: number, visible: boolean) => {
    setParsedItems((prevItems) =>
      prevItems.map((item) => (item.id !== id ? item : { ...item, isCommentVisible: visible })),
    );
  };

  const updateParsedItem = (id: number, update: Partial<PreviewItemUpdateData>) => {
    setParsedItems((prevItems) => {
      const indexToUpdate = prevItems.findIndex((item) => item.id === id);
      let updatedItem: PreviewItem;
      if (update.sapMaterialNumber && update.designation && update.inputText) {
        if (update.inputText.length === 15 && /\d{2}/.test(update.inputText.substring(13))) {
          // There's something that looks like a pack code
          const packCode = update.inputText.substring(13);
          updatedItem = {
            ...prevItems[indexToUpdate],
            productId: update.sapMaterialNumber + packCode,
            marketType: update.marketType ?? null,
          };
        } else {
          // Replace with designation - we cannot determine the pack code in any way
          updatedItem = {
            ...prevItems[indexToUpdate],
            productId: update.designation,
            marketType: update.marketType ?? null,
          };
        }
      } else if (update.designation) {
        // Let's try sending the designation...
        updatedItem = {
          ...prevItems[indexToUpdate],
          productId: update.designation,
        };
      } else {
        // Old case - just copy over the data that came in
        // Update with local data...
        updatedItem = { ...prevItems[indexToUpdate], ...update };
      }

      if (!("previousProductId" in update)) {
        updatedItem.previousProductId = undefined;
      }

      const newItems = prevItems.map((item, index) => (index !== indexToUpdate ? item : updatedItem));
      debouncedApiUpdatePreviewEntry(indexToUpdate, updatedItem);
      return newItems;
    });
  };

  const updateParsedItemsPriceReference = async (priceReference: string) => {
    let productsCount = 0;

    const itemUpdates = parsedItems.map((item) => {
      // Skip if sales are enabled
      if (item.onSale) {
        return item;
      }

      productsCount++;
      const updatedItem: PreviewItem = { ...item, priceReference };
      return updatedItem;
    });

    await apiUpdatePreviewEntryBulk(itemUpdates);

    handleOpenPriceRefSnackBar(productsCount);
  };

  const switchParsedItemsProductVersion = async () => {
    let productsCount = 0;
    const updatedItems = await Promise.all(
      parsedItems.map(async (item, index) => {
        if (!item.succeedingProductDesignation) {
          return item;
        }

        const updatedItem: PreviewItem = {
          ...item,
          ...{
            productId: item.succeedingProductDesignation,
            inputText: item.succeedingProductDesignation,
            previousProductId: item.productId,
            succeedingProductDesignation: "",
          },
        };
        productsCount += 1;
        await apiUpdatePreviewEntry(index, updatedItem);
        return updatedItem;
      }),
    );
    setParsedItems(updatedItems);
    handleOpenSwitchProductSnackBar(productsCount);
  };

  const closeCb = mergeCartFlow
    ? () => {
        apiClearMergeCartData();
        onClose();
      }
    : onClose;

  const isDataLoaded = state === BulkUploadState.UPLOAD_SUCCESS || state === BulkUploadState.ERROR_CART;
  const isCartError = state === BulkUploadState.ERROR_CART;
  const showLoadingSpinner = state === BulkUploadState.LOADING_CART || state === BulkUploadState.UPLOADING;
  const itemsWithTargetPriceMessages = isTargetPriceEnabled() ? attachTargetPriceMessages(parsedItems, t) : parsedItems;
  const hasErrorsInParsedItems = itemsWithTargetPriceMessages.some((item) =>
    item.messages.some((message) => message.type === "error"),
  );
  const hasTargetPriceError = isTargetPriceEnabled()
    ? itemsWithTargetPriceMessages.some((item) =>
        item.messages.some((message) => message.code === TARGET_PRICE_ERROR_CODE),
      )
    : false;

  if (showOverrideConfirmation) {
    return (
      <DeleteCartConfirmationModal
        onCancel={doHideOverrideConfirmation}
        onOk={() => doOverrideCart(showOnSaleProductsOnly)}
      />
    );
  }

  const content =
    state === BulkUploadState.UPLOAD_SUCCESS ? (
      <BulkAtcPopupPreviewStep
        items={itemsWithTargetPriceMessages}
        onDataChange={updateParsedItem}
        onRemoveItem={removeParsedItem}
        onChangeAllCommentsVisibility={setAllParsedItemsCommentVisibility}
        onChangeCommentVisibility={setParsedItemCommentVisibility}
        onRemoveAllItems={removeAllParsedItems}
        saleOnly={showOnSaleProductsOnly}
        setSaleOnly={setOnSaleProductsOnly}
      />
    ) : (
      <div className="pt-1">
        <Grid container columns={2}>
          <Grid size={1}>
            <FileUploadSection
              state={state}
              source={source}
              onFileSelected={handleFileSelected}
              messages={getMessages(BulkUploadSource.FILE)}
            />
          </Grid>
          <Grid size={1}>
            <TextUploadSection
              state={state}
              source={source}
              onTextSubmit={handleTextSelected}
              messages={getMessages(BulkUploadSource.TEXT)}
            />
          </Grid>
        </Grid>
        {(isCartError || showLoadingSpinner) && (
          <MessageContainerBox>
            <div className={"d-flex flex-justify-between"}>
              {isCartError && (
                <Alert className={"mt-2"} severity={"error"}>
                  {t("bulkUpload.error.webservice")}
                </Alert>
              )}
              {showLoadingSpinner && <Spinner className={"mt-2 w-100"} />}
            </div>
          </MessageContainerBox>
        )}
        <HelpSection />
      </div>
    );

  const title = mergeCartFlow ? t("feature.mergeCart.popup.title") : t("cartPage.links.bulkAdd");

  const header = (
    <div className="scha__modal__bulk-preview__header">
      <div className="scha__modal__bulk-preview__header-title">{title}</div>
      <Stack direction={"row"} gap={1}>
        <ChoosePriceReferenceForCart
          className="bulk-preview__header-btn"
          updatePriceReference={updateParsedItemsPriceReference}
        />
        {!isPhaseOut2Enabled() && (
          <SwitchProductVersions
            className="bulk-preview__header-btn"
            switchProductVersion={switchParsedItemsProductVersion}
          />
        )}
      </Stack>
    </div>
  );

  return (
    <Modal
      onClose={closeCb}
      headerOverride={isDataLoaded ? header : undefined}
      footerChildren={getFooter({
        areBtnsDisabled: !isDataLoaded || hasErrorsInParsedItems || hasTargetPriceError,
        showOverrideBtn: cartItemAmount > 0 && !mergeCartFlow,
        onOverride: doShowOverrideConfirmation,
        onAddToCart: () => doAddToCart(showOnSaleProductsOnly),
        onClose: closeCb,
        t,
      })}
      size={isDataLoaded ? ModalSize.LARGE : ModalSize.MEDIUM}
      title={!isDataLoaded ? title : ""}
      modalContentClassName="scha__modal__bulk-preview__content"
    >
      {content}
      <Snackbar
        open={snackbarData.open}
        message={snackbarData.message}
        onClose={() => setSnackbarData((prev) => ({ ...prev, open: false }))}
        action={<SnackbarCloseButton onClick={() => setSnackbarData((prev) => ({ ...prev, open: false }))} />}
      />
    </Modal>
  );
};

interface FooterProps {
  showOverrideBtn: boolean;
  areBtnsDisabled: boolean;
  onOverride: () => void;
  onAddToCart: () => void;
  onClose: () => void;
  t: WithTranslation["t"];
}
const getFooter = (props: FooterProps) => {
  const { showOverrideBtn, t, onAddToCart, onOverride, areBtnsDisabled } = props;

  const closeBtn = (
    <Button onClick={props.onClose} variant={"text"} data-test-id={"bulk-upload-btn-cancel"}>
      {props.t("bulkUpload.buttons.cancel")}
    </Button>
  );

  const webservicesDisabled = window.SCHA.config.disableAllWS;

  return (
    <PageToolbar
      noBottomSpace={true}
      left={showOverrideBtn && closeBtn}
      className="w-100"
      right={
        <Stack direction={"row"} gap={1}>
          {!showOverrideBtn && closeBtn}

          {showOverrideBtn && (
            <Button
              className="bulk-preview__footer-btn"
              color={"secondary"}
              onClick={onOverride}
              disabled={webservicesDisabled || areBtnsDisabled}
              data-test-id={"bulk-upload-btn-override-cart"}
            >
              {t("bulkUpload.buttons.override")}
            </Button>
          )}
          <Button
            className="bulk-preview__footer-btn"
            startIcon={<DefaultButtonIcon type={IconType.Cart} />}
            onClick={onAddToCart}
            disabled={webservicesDisabled || areBtnsDisabled}
            data-test-id={"bulk-upload-btn-add-to-cart"}
          >
            {t("contractDetailsPage.addToCart")}
          </Button>
        </Stack>
      }
    />
  );
};

export default BulkATCPopup;

function resolveErrorLabels(t: WithTranslation["t"], error: { reason: string; params: string[] }) {
  return [
    {
      type: "error",
      label: t(error.reason, {
        count: error.params.length,
        bulkMaxRows: window.SCHA.config.bulkMaxRows,
        param: transformErrorParam(t, error.params),
      }),
    },
  ] as { type: "error"; label: string }[];
}

function transformErrorParam(t: WithTranslation["t"], params: string[]) {
  if (params.length === 0) {
    return "";
  }
  if (params.length === 1) {
    return params[0];
  }
  const len = params.length;
  const lastParam = params[len - 1];
  params = params.slice(0, len - 1);
  return `${params.join(", ")} ${t("common.and")} ${lastParam}`;
}
const TARGET_PRICE_ERROR_CODE = "bulkUpload.error.targetPrice.invalid";
function hasTargetPriceIssue(item: PreviewItem): boolean {
  if (item.onSale) {
    return false;
  }
  const hasServerError = item.messages.some((message) => message.code === TARGET_PRICE_ERROR_CODE);
  const tp = item.targetPrice;
  if (tp === undefined || tp === null) {
    return hasServerError;
  }
  const num = Number(tp);
  const numericInvalid = !Number.isFinite(num) || num <= 0;
  return hasServerError || numericInvalid;
}
function attachTargetPriceMessages(items: PreviewItem[], t: WithTranslation["t"]): PreviewItem[] {
  return items.map((item) => {
    if (!hasTargetPriceIssue(item)) {
      return item;
    }
    const hasExistingTargetPriceMessage = item.messages.some((message) => message.code === TARGET_PRICE_ERROR_CODE);
    if (hasExistingTargetPriceMessage) {
      return item;
    }
    const targetPriceMessage: MessageModel = {
      type: "error",
      code: TARGET_PRICE_ERROR_CODE,
      params: {},
    };
    return {
      ...item,
      messages: [...item.messages, targetPriceMessage],
    };
  });
}
